import { CompactTable } from '@table-library/react-table-library/compact';
import { useTheme } from '@table-library/react-table-library/theme';
import { getTheme } from '@table-library/react-table-library/baseline';
import { usePagination } from "@table-library/react-table-library/pagination";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiEye, FiFilter } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { resetAppointmentNotification } from "../../store/notification/notificationActions";
import { getAppointmentList } from "../../store/appointment/appointmentActions";
import { getImage } from "../../utils";
import moment from "moment";
import NotFoundWidget from '../../components/widgets/NotFound';
import { TablePagination } from '../../components/global/table-pagination';
import { COURSE_DETAILS_URL } from '../../components/app/routes';

const CourseList = () => {
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState([]);
    const [nodes, setNodes] = useState([]);

    const handleSearch = (event) => {
        setSearch(event.target.value);
    };

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(getAppointmentList({}))
            .unwrap()
            .then((v) => setNodes(v))
        dispatch(resetAppointmentNotification())
    }, [dispatch])

    const statusMeta = {
        [-1]: { label: "Pending", className: "bg-nt-blue text-white" },
        [0]: { label: "Pending Payment", className: "bg-indigo-500 text-white" },
        [1]: { label: "Done", className: "bg-green text-white" },
        [2]: { label: "Canceled", className: "bg-red text-white" },
        [3]: { label: "Under Review", className: "bg-amber-500 text-white" },
        [4]: { label: "Payment Rejected", className: "bg-red opacity-80 text-white" },
    };

    const COLUMNS = [
        {
            label: 'Avatar', class: "avatar", renderCell: (item) => {
                return <img className="h-[50px] w-[50px] rounded-full ring-2 ring-white shadow-sm" src={getImage(item.avatar, "AVATAR")} />
            },
            pinLeft: true,
        },
        {
            label: 'Name',
            renderCell: (item) => <p className="capitalize font-medium text-nt-blue">{item.fullname}</p>,
            pinLeft: true,

        },
        {
            label: 'Price',
            renderCell: (item) => item.price ? item.price + " €" : '',
            pinLeft: true,

        },
        {
            label: 'Date',
            renderCell: (item) => {
                return (
                    <span className="font-medium text-nt-blue">
                        {moment(item.date).utc().format("YYYY-MM-DD")}
                    </span>
                )
            },
            pinLeft: true,
        },
        {
            label: 'Time',
            renderCell: (item) => moment(item.date).parseZone().format("HH:mm").toString() + " - " + moment(item.date).parseZone().add(50, "minutes").format("HH:mm").toString(),
            pinLeft: true,

        },
        {
            label: 'Status', renderCell: (item, index) => {
                return (
                    <div className="flex flex-wrap gap-1.5">
                        <AppointmentStatus status={item.status} />

                    </div>
                )
            },
            pinLeft: true,

        },
        {
            label: '',
            renderCell: (item, index) => {
                return <Link className="inline-flex w-auto items-center justify-between gap-1.5 rounded-[5px] border border-nt-blue px-2 py-1.5 text-sm font-medium text-nt-blue no-underline hover:bg-nt-blue hover:text-white" to={`${COURSE_DETAILS_URL}/${item._id}`}>
                    <FiEye />
                    <span>View</span>
                </Link>
            },
            pinLeft: true,

        },
    ];

    const AppointmentStatus = ({ status }) => {
        const meta = statusMeta[status] || { label: "Unknown", className: "bg-nt-grey text-white" };
        return <span className={`${meta.className} px-2.5 py-1 rounded-full text-xs font-semibold`}>{meta.label}</span>
    }

    const theme = useTheme([getTheme(), {
        HeaderRow: `color: #191D51;`,
        Table: `--data-table-library_grid-template-columns: 110px minmax(180px,1.2fr) 120px 160px 190px 180px 130px; width:auto;min-width:auto;min-height:auto;`
    }]);

    const filteredNodes = useMemo(
        () => nodes.filter((item) =>
            (item.fullname.toLowerCase().includes(search.toLowerCase()) ||
                item.date.toLowerCase().includes(search.toLowerCase())) &&
            ((filter.length > 0 && filter.includes(item.status)) || filter.length === 0)
        ),
        [nodes, search, filter]
    );

    const data = useMemo(
        () => ({
            nodes: filteredNodes.map((node) => ({
                ...node,
                id: node.id ?? node._id,
            })),
        }),
        [filteredNodes]
    );

    const pagination = usePagination(data, {
        state: {
            page: 0,
            size: 5,
        },
    });

    const pageButtonCount = 2;
    const totalPages = pagination.state.getTotalPages(data.nodes)
    const pages = pagination.state.getPages(data.nodes);

    const toggleFilter = (status) => {
        pagination.fns.onSetPage(0)
        if (filter.includes(status))
            setFilter(x => x.filter(v => v != status))
        else
            setFilter(x => [...x, status])
    }

    const allStatusFilters = [-1, 0, 1, 2, 3, 4];

    return (
        <>
            <div className="rounded-[20px] bg-white p-[30px]">
                <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-nt-grey">Track upcoming classes, status changes, and payment progress.</p>
                    </div>
                    <label htmlFor="search" className="w-full sm:w-auto">
                        <input
                            id="search"
                            type="text"
                            placeholder="Search by name or date..."
                            className="w-full border border-nt-grey-light rounded-xl bg-white px-3 py-2 sm:min-w-[260px]"
                            value={search}
                            onChange={handleSearch}
                        />
                    </label>
                </div>
                <div className="rounded-xl border border-nt-grey-light bg-slate-50 p-3 mb-5">
                    <div className="flex flex-wrap gap-2 items-center">
                        <FiFilter className="text-nt-blue mr-1" size={18} />
                        {allStatusFilters.map((status) => {
                            const active = filter.includes(status);
                            const label = statusMeta[status].label;
                            return (
                                <button
                                    key={status}
                                    type="button"
                                    onClick={() => toggleFilter(status)}
                                    className={`text-xs sm:text-sm border select-none rounded-full py-1.5 px-3 transition font-medium ${active
                                        ? "text-white bg-nt-blue border-nt-blue shadow-sm"
                                        : "text-nt-blue bg-white border-nt-grey-light hover:border-nt-blue"
                                        }`}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="mt-2 text-xs text-nt-grey">
                        Showing <span className="font-semibold text-nt-blue">{filteredNodes.length}</span> of{" "}
                        <span className="font-semibold text-nt-blue">{nodes.length}</span> appointments
                    </div>
                </div>

                <div className="rounded-xl border border-nt-grey-light overflow-hidden bg-white">
                    <CompactTable columns={COLUMNS} layout={{ custom: true, horizontalScroll: true }} data={data} theme={theme} pagination={pagination} />
                </div>

                {filteredNodes.length === 0 ? <NotFoundWidget message="No appointments match your current filters." /> : null}
                {totalPages > 0 ? <TablePagination
                    pageButtonCount={pageButtonCount}
                    totalPages={totalPages}
                    pagination={pagination}
                    pages={pages}
                /> : null}
            </div>

        </>
    )
}

export { CourseList }